import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/teacher-real.jpg";

export const therapistsCoaches: Persona = {
  slug: "therapists-coaches",
  profession: { hu: "terapeuta", en: "therapist" },
  professionPlural: {
    hu: "terapeuták és coachok",
    en: "therapists and coaches",
  },
  professionGenitive: { hu: "terapeutaként", en: "as a therapist" },
  heroH1: {
    hu: "A te dolgod a kliens. A többit ránk bízhatod.",
    en: "Your job is the client. Leave the rest to us.",
  },
  heroSub: {
    hu: "Saját weboldal, online időpontfoglalás, csomagok és bérletek, automatikus emlékeztetők és számlázás: egy helyen, terapeutáknak és coachoknak.",
    en: "Personal website, online booking, packages and passes, automatic reminders and invoicing: all in one place, for therapists and coaches.",
  },
  heroImage,
  heroImageAlt: "Therapist in an intimate session with a client",
  chatExamplePrompt: {
    hu: "Tegyél be egy önismereti coaching ülést",
    en: "Book a self-awareness coaching session",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem szerda 18:00, 75 perc önismereti coaching. A kliensnek visszaigazolást és előzetes munkafüzetet küldtem.",
    en: "Booked Wednesday 6 PM, 75-minute self-awareness coaching. Confirmation and prep workbook sent to the client.",
  },
  accentColor: "teal",

  defaultMode: "solo",
  teamSize: { min: 1, max: 10 },
  soloLabel: { hu: "Szóló terapeuta", en: "Solo therapist" },
  teamLabel: {
    hu: "Terápiás központ / coaching csapat",
    en: "Therapy center / coaching team",
  },

  soloDayInLife: [
    {
      time: "08:30",
      what: {
        hu: "Reggeli felkészülés. Az AI riportot küld: 1 új lead diszkrét kérdőívvel, 4 ülés ma.",
        en: "Morning prep. AI report: 1 new lead with discreet questionnaire, 4 sessions today.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "75 perc coaching Annával. Korábbi közös céljaitok és haladási jegyzetek előre elő vannak készítve.",
        en: "75-minute coaching with Anna. Your past goals and progress notes are ready beforehand.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új klient lefoglalja a bevezető hívást. AI automatikusan elküldi az önismereti előkérdőívet.",
        en: "A new client books the intro call. AI automatically sends the self-awareness intake form.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Online ülés Google Meeten. A link minden alkalomra egyedi, jelszavas.",
        en: "Online session on Google Meet. Every link is unique and password-protected.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 4 ülés, 88 000 Ft bevétel. Heti reflexiós kérdéseket küldsz a klienseknek.",
        en: "End of day: 4 sessions, 88,000 HUF revenue. You send weekly reflection questions to clients.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Központ dashboard: 4 terapeutád 14 ülést tart ma. Mindenki saját nézettel.",
        en: "Center dashboard: your 4 therapists have 14 sessions today. Each with their own view.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Új klient párkapcsolati nehézséggel: az AI a párterápiára szakosodott Bencéhez osztja.",
        en: "New client with relationship issues: AI routes to Bence (couples therapy specialist).",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy terapeuta beteg. Az AI értesíti az érintett klienseket, felajánl új időpontot vagy másik kollégát.",
        en: "A therapist is sick. AI notifies affected clients, offers new times or another colleague.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Heti riport: ülés-szám, klient-elégedettség, visszatérési arány. Bizalmas adatok elkülönítve.",
        en: "Weekly report: session count, client satisfaction, return rate. Confidential data is siloed.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Workshop-akció telik ('Önismereti hétvége': 8 fő). Az AI várólistára teszi a túljelentkezőket.",
        en: "Workshop promo fills up ('Self-awareness weekend': 8 people). AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Hónap vége: bevétel terapeutánként, jutalék-elszámolás, számlák kiállítva.",
        en: "Month-end: revenue per therapist, commission, invoices issued.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: {
        hu: "Diszkrét foglalási felület",
        en: "Discreet booking interface",
      },
      body: {
        hu: "Csak a szabad slot-ok látszanak. Jelszavas védelem opció a kliens-oldalon.",
        en: "Only free slots are visible. Password protection option on the client side.",
      },
    },
    {
      icon: "Bot",
      title: { hu: "Előkérdőív szűrő", en: "Intake form filter" },
      body: {
        hu: "Új érdeklődő kitölt egy diszkrét előkérdőívet, automata válasz-email megy ki: te csak az értékelt leadekre figyelsz.",
        en: "New leads fill out a discreet intake form, an auto-reply email goes out: you only focus on qualified leads.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Titkosított jegyzetek", en: "Encrypted notes" },
      body: {
        hu: "Minden ülés-jegyzet end-to-end titkosítva. Csak te férsz hozzá, EU-szervereken tárolva.",
        en: "Every session note is end-to-end encrypted. Only you can access them, stored on EU servers.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan terapeuta, egy ár",
        en: "Unlimited therapists, one price",
      },
      body: {
        hu: "1 vagy 10 fő: ugyanaz a havidíj. Mindenki saját naptárral és kliens-bázissal.",
        en: "1 or 10: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: {
        hu: "Szigorú jogosultság-kezelés",
        en: "Strict role-based access",
      },
      body: {
        hu: "Kliens-adatokhoz CSAK az érintett terapeuta fér hozzá. Te tulajdonosként az aggregált statisztikát látod, de a jegyzeteket nem.",
        en: "Only the assigned therapist accesses client data. As owner you see aggregated stats: not notes.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a klienseket", en: "AI assigns clients" },
      body: {
        hu: "Új lead: az AI specialitás (önismereti coaching, párterápia, gyász-coaching) alapján a megfelelő kollégához osztja.",
        en: "New lead: AI assigns by specialty (self-awareness, couples, grief coaching) to the right colleague.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus bevétel és jutalék",
        en: "Automatic revenue and commission",
      },
      body: {
        hu: "Ki mennyit dolgozott, mekkora kliens-haladást ért el: exportálható, Számlázz.hu kompatibilis.",
        en: "Who worked how much, what client progress they achieved: exportable, Számlázz.hu compatible.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Online és személyes is", en: "Online and in-person" },
      body: {
        hu: "Egy naptár mindkettőhöz. Google Meet link automatikus, terem-foglalás integrált.",
        en: "One calendar for both. Auto Google Meet link, integrated room booking.",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Csomag-értékesítés", en: "Package sales" },
      body: {
        hu: "10 alkalmas önismereti folyamat, 6 hónapos kísérő coaching: egy oldalon, online fizetéssel.",
        en: "10-session self-awareness journey, 6-month companion coaching: one page, online payment.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "Diszkrét emlékeztetők", en: "Discreet reminders" },
      body: {
        hu: "Email/SMS/WhatsApp emlékeztető foglalás előtt: kevesebb no-show, kevesebb adminisztráció.",
        en: "Email/SMS/WhatsApp reminders before bookings: fewer no-shows, less admin.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI follow-up kampányok", en: "AI follow-up campaigns" },
      body: {
        hu: "Ha egy klient kiesik a ritmusból, az AI automata emlékeztetőt vagy újrafoglalási ajánlatot küld: te állítod be, a rendszer kiküldi.",
        en: "If a client drops off, AI sends an automatic reminder or rebooking offer: you configure, the system sends.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Bizalmas munkát végzek: biztonságos?",
        en: "I do confidential work: is it secure?",
      },
      answer: {
        hu: "Igen. EU-szerverek, GDPR-megfelelőség, end-to-end titkosítás minden jegyzetnél. Az adatok SOHA nem kerülnek 3. félhez, AI training-re sem.",
        en: "Yes. EU servers, GDPR-compliant, end-to-end encryption on every note. Data is NEVER shared with 3rd parties, never used for AI training.",
      },
    },
    {
      question: {
        hu: "Központban dolgozunk több terapeutával: hogy kezeli a csapatot?",
        en: "We work in a center with multiple therapists: how does it handle the team?",
      },
      answer: {
        hu: "Minden terapeutának saját naptára, kliens-bázisa, titkosított jegyzetei vannak. Te tulajdonosként az AGGREGÁLT statisztikát látod, de a kliens-adatokhoz NEM férsz hozzá. Csak az érintett terapeuta láthatja a saját klienseit. Korlátlan terapeutát vehetsz fel, NEM fizetsz többet.",
        en: "Each therapist has their own calendar, clients and encrypted notes. As owner you see AGGREGATED stats but do NOT access client data. Only the assigned therapist sees their clients. Add unlimited therapists: you do NOT pay more.",
      },
    },
    {
      question: {
        hu: "Az emberi kapcsolat a lényeg, nem szeretném gépiesíteni",
        en: "Human connection is the point: I don't want to mechanize it",
      },
      answer: {
        hu: "Az automata CSAK a logisztikát végzi (foglalás, emlékeztető, számla). Az ülés és a kapcsolat 100%-ban a tied. Sőt: kevesebb adminisztráció = több energia a klienseknek.",
        en: "Automation ONLY handles logistics (booking, reminders, invoicing). The session and relationship are 100% yours. Less admin = more energy for clients.",
      },
    },
    {
      question: {
        hu: "Csoportos workshopokat is tartok: kezeli?",
        en: "I also run group workshops: does it handle them?",
      },
      answer: {
        hu: "Igen. Workshop-esemény külön foglalási oldallal, létszám-korláttal, várólistával, csoport-emaillel. A workshop bevétele a normál statisztikába is bekerül.",
        en: "Yes. Workshop events with their own booking page, capacity, waitlist, group email. Workshop revenue flows into your normal stats.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: {
        hu: "Egyéni terápia és coaching",
        en: "Individual therapy and coaching",
      },
      examples: [
        { hu: "75 perc önismereti ülés", en: "75-min self-awareness session" },
        { hu: "Bevezető konzultáció", en: "Intro consultation" },
        { hu: "Online ülés", en: "Online session" },
      ],
      priceHint: {
        hu: "Példa: 50 ülés × 22 000 Ft = 1 100 000 Ft / hó",
        en: "Example: 50 sessions × 22,000 HUF = 1,100,000 HUF / month",
      },
    },
    {
      icon: "Users",
      title: {
        hu: "Pár- és csoport-coaching",
        en: "Couples and group coaching",
      },
      examples: [
        { hu: "Párcoaching (90 perc)", en: "Couples coaching (90 min)" },
        {
          hu: "Kis-csoportos önismereti kör (6 fő)",
          en: "Small-group circle (6 ppl)",
        },
        { hu: "Női kör", en: "Women's circle" },
      ],
      priceHint: {
        hu: "Példa: 18 ülés × 35 000 Ft = 630 000 Ft / hó",
        en: "Example: 18 sessions × 35,000 HUF = 630,000 HUF / month",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Coaching csomagok", en: "Coaching packages" },
      examples: [
        {
          hu: "10 alkalmas önismereti folyamat",
          en: "10-session self-awareness journey",
        },
        { hu: "6 hónapos kísérő coaching", en: "6-month companion coaching" },
        { hu: "Gyász-feldolgozó program", en: "Grief processing program" },
      ],
      priceHint: {
        hu: "Példa: 8 csomag × 195 000 Ft = 1 560 000 Ft / hó",
        en: "Example: 8 packages × 195,000 HUF = 1,560,000 HUF / month",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Workshopok és anyagok", en: "Workshops and materials" },
      examples: [
        {
          hu: "Hétvégi önismereti workshop",
          en: "Weekend self-awareness workshop",
        },
        { hu: "Munkafüzet (önismeret)", en: "Workbook (self-awareness)" },
        { hu: "Vezetett meditáció hanganyag", en: "Guided meditation audio" },
      ],
      priceHint: {
        hu: "Példa: 1 workshop × 12 fő × 38 000 Ft = 456 000 Ft",
        en: "Example: 1 workshop × 12 people × 38,000 HUF = 456,000 HUF",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Tagság és bérletek", en: "Membership and passes" },
      examples: [
        { hu: "Havi 2 ülés tagság", en: "Monthly 2-session membership" },
        { hu: "VIP kísérő program", en: "VIP companion program" },
        {
          hu: "Éves önismereti kísérő csomag",
          en: "Annual self-awareness package",
        },
      ],
      priceHint: {
        hu: "Központ-szint: 4 terapeuta × 14 tagsági klient × 38 000 Ft = 2 128 000 Ft / hó potenciál",
        en: "Center level: 4 therapists × 14 member clients × 38,000 HUF = 2,128,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+48%",
      label: { hu: "csomag-eladás (6 ülés)", en: "package sales (6 sessions)" },
    },
    {
      value: "-75%",
      label: { hu: "email-egyeztetés", en: "email back-and-forth" },
    },
    { value: "4.9★", label: { hu: "kliens-NPS", en: "client NPS" } },
    {
      value: "100%",
      label: { hu: "GDPR + diszkréció", en: "GDPR + discretion" },
    },
  ],
  testimonial: {
    quote: {
      hu: "Az önismereti workshopjaim és 1-1 ülések egy rendszerben futnak. A kliens online foglal, az AI emlékeztet, én csak tartom a tereket.",
      en: "My self-awareness workshops and 1:1 sessions run in one system. The client books online, AI reminds, I just hold the space.",
    },
    name: "Nagy Boglárka",
    role: {
      hu: "Önismereti coach, terapeuta",
      en: "Self-awareness coach, therapist",
    },
    city: { hu: "Budapest, IX. kerület", en: "Budapest, District IX" },
    avatarInitials: "NB",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a kínálatod",
        en: "Tell us your offerings in chat",
      },
      body: {
        hu: "Egyéni coaching, workshop, csoport, intenzív hétvége: az AI strukturálja.",
        en: "Individual coaching, workshops, groups, intensive weekends: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a coach-oldalt",
        en: "AI builds your coach site",
      },
      body: {
        hu: "Csomagok, foglalás, kérdőív, workshop-jegyek.",
        en: "Packages, booking, intake form, workshop tickets.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Migráljuk a meglévő klienseket",
        en: "We migrate your existing clients",
      },
      body: {
        hu: "Diszkréten, titkosítva, GDPR-megfelelően.",
        en: "Discreetly, encrypted, GDPR-compliant.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, online foglalás + workshop-jegy",
        en: "Live, online booking + workshop tickets",
      },
      body: {
        hu: "Várólista, automatikus emlékeztető, követés.",
        en: "Waitlist, auto-reminders, follow-up.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Google Meet",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: { hu: "Terapeuták és coachok", en: "Therapists & coaches" },
  similarPersonas: [
    "psychologists-mentors",
    "consultants-business-coaches",
    "nutritionists",
  ],
};
